import React, { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

const StatsCard = ({ tag, count, url, children }) => {
  const [isVisible, setIsVisible] = useState(false);
  const [displayCount, setDisplayCount] = useState(0);
  const cardRef = useRef(null);
  const navigate = useNavigate();

  // Intersection Observer Logic
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsVisible(true);
          observer.disconnect();
        }
      },
      { threshold: 0.1 }
    );

    if (cardRef.current) observer.observe(cardRef.current);
    return () => observer.disconnect();
  }, []);

  // Number Counter Logic
  useEffect(() => {
    if (isVisible) {
      let start = null;
      const duration = 2000; // Slower, more "fluid" count-up
      const targetCount = count;

      const step = (timestamp) => {
        if (!start) start = timestamp;
        const progress = timestamp - start;
        const percentage = Math.min(progress / duration, 1);
        
        // Use an "Out-Expo" easing function for a smoother finish
        const easeOutQuad = 1 - Math.pow(1 - percentage, 3);
        const newCount = Math.floor(easeOutQuad * targetCount);
        
        setDisplayCount(newCount);
        if (progress < duration) {
          requestAnimationFrame(step);
        }
      };
      requestAnimationFrame(step);
    }
  }, [isVisible, count]);

  return (
    <div 
      ref={cardRef}
      onClick={() => url && navigate(url)}
      className="group relative w-full sm:w-64"
    >
      {/* Background Glow Effect on Hover */}
      <div className="absolute -inset-1 bg-gradient-to-r from-blue-600 to-indigo-600 rounded-2xl blur opacity-25 group-hover:opacity-75 transition duration-1000 group-hover:duration-200"></div>
      
      {/* Main Card */}
      <div className="relative flex flex-col items-center justify-center p-8 bg-white rounded-2xl border border-gray-100 leading-none transition-all duration-300 group-hover:translate-y-[-4px] cursor-pointer">
        
        {/* Icon Circle */}
        <div className="mb-4 p-3 rounded-full bg-blue-50 text-blue-600 group-hover:bg-blue-600 group-hover:text-white transition-colors duration-300">
          {children}
        </div>

        {/* Count */}
        <div className="text-4xl sm:text-5xl font-black text-gray-900 mb-2">
          {displayCount}
          <span className="text-blue-600 group-hover:animate-pulse">+</span>
        </div>

        {/* Tag */}
        <div className="text-xs font-bold uppercase tracking-[0.2em] text-gray-400 group-hover:text-gray-600 transition-colors">
          {tag}
        </div>
      </div>
    </div>
  );
};

export default StatsCard;